import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { signAwsRequest } from '../../rtk/auth/signRequest';

const CLOUDFRONT_SESSION_URL = process.env.REACT_APP_CF_SESSION_URL;

/**
 * How old a session may get before we mint a new one.
 *
 * The signed cookie's policy expires exactly one hour after it is issued, and
 * so does the Cognito id token needed to sign the request that mints it. So the
 * refresh has to land comfortably inside that hour: too late and the token we
 * would sign with is already dead, leaving the iframe to load an expired
 * session (CloudFront answers 403, the SPA error mapping turns that into the
 * app shell, and the user sees a sign-in page embedded in the guide).
 */
const SESSION_MAX_AGE_MS = 45 * 60 * 1000;

/** How often to check the session's age. Also catches a machine waking up. */
const AGE_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** Renew the id token when it has less than this left. */
const TOKEN_MIN_REMAINING_MS = 5 * 60 * 1000;

export default function CloudFrontSessionInitializer() {
  const auth = useAuth();

  // Guard against duplicate calls (React StrictMode double-invoke and token
  // refreshes that don't actually change the identity).
  const initializedToken = useRef(null);
  // When the current signed cookie was minted, so its age can be checked
  // against the policy lifetime without reading the cookie (it is HttpOnly).
  const mintedAtRef = useRef(0);
  const inFlightRef = useRef(false);

  // Read inside callbacks that outlive the render they were created in.
  const authRef = useRef(auth);
  useEffect(() => {
    authRef.current = auth;
  }, [auth]);

  /**
   * An id token with enough life left to sign a minting request, renewing it
   * first when it is close to expiry. Returns null when no session is possible.
   */
  const getUsableIdToken = useCallback(async () => {
    const current = authRef.current;
    const user = current?.user;
    if (!user?.id_token) return null;

    const remainingMs = (user.expires_at ?? 0) * 1000 - Date.now();
    if (remainingMs > TOKEN_MIN_REMAINING_MS) return user.id_token;

    // Cognito's code flow issues a refresh token, so this renews without an
    // iframe. If it fails the user's session is genuinely over.
    const renewed = await current.signinSilent();
    return renewed?.id_token ?? null;
  }, []);

  const createCloudFrontSession = useCallback(async () => {
    if (!CLOUDFRONT_SESSION_URL || inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      const idToken = await getUsableIdToken();
      if (!idToken) return;

      // SigV4-sign the request: the session endpoint is IAM-protected
      // execute-api, so it needs a signed request, not a Bearer header.
      const signed = await signAwsRequest({
        url: CLOUDFRONT_SESSION_URL,
        idToken,
        method: 'POST',
        headers: { Accept: 'application/json' },
      });

      const response = await fetch(signed.url, {
        method: signed.method,
        headers: signed.headers,
        body: signed.body,
        credentials: 'include',
      });

      if (!response.ok) {
        // Allow a retry on the next effect run or age check.
        initializedToken.current = null;
        throw new Error(`CloudFront session failed (${response.status})`);
      }

      initializedToken.current = idToken;
      mintedAtRef.current = Date.now();
    } catch (err) {
      initializedToken.current = null;
      console.error('[CloudFrontSession] create failed', err);
    } finally {
      inFlightRef.current = false;
    }
  }, [getUsableIdToken]);

  // Mint on sign-in, and again whenever the identity changes.
  useEffect(() => {
    const idToken = auth.user?.id_token;
    if (auth.isLoading || !auth.isAuthenticated || !idToken || !CLOUDFRONT_SESSION_URL) return;
    if (initializedToken.current === idToken) return;

    createCloudFrontSession();
  }, [auth.isLoading, auth.isAuthenticated, auth.user?.id_token, createCloudFrontSession]);

  // Re-mint before the signed cookie's hour is up. Polling the age rather than
  // scheduling one timer at the deadline means a laptop that slept through the
  // deadline refreshes as soon as it wakes, instead of on a timer that never
  // fired.
  useEffect(() => {
    if (!auth.isAuthenticated || !CLOUDFRONT_SESSION_URL) return undefined;

    const refreshIfStale = () => {
      if (document.visibilityState === 'hidden') return;
      if (!mintedAtRef.current) return;
      if (Date.now() - mintedAtRef.current < SESSION_MAX_AGE_MS) return;
      createCloudFrontSession();
    };

    const interval = setInterval(refreshIfStale, AGE_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', refreshIfStale);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshIfStale);
    };
  }, [auth.isAuthenticated, createCloudFrontSession]);

  return null;
}
